import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../hooks/useAuthState';
import { getEvent } from '../lib/firestore/events';
import { processCheckIn, getCurrentCheckInToken, generateQRCode, generateCheckInURL } from '../lib/qr-checkin';
import '../styles/CheckIn.css';

const CheckIn = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuthState();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [result, setResult] = useState(null);
  const [plusOnes, setPlusOnes] = useState(0);

  useEffect(() => {
    if (eventId) {
      loadEvent();
      const urlToken = searchParams.get('token');
      if (urlToken) {
        setToken(urlToken);
      }
    }
  }, [eventId, searchParams]);

  const loadEvent = async () => {
    if (!eventId) return;

    try {
      const eventData = await getEvent(eventId);
      setEvent(eventData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading event:', error);
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    if (!user || !eventId || !token) return;

    setCheckingIn(true);
    setResult(null);

    try {
      const checkInResult = await processCheckIn(eventId, token, user.uid, plusOnes);
      setResult(checkInResult);

      if (checkInResult.success) {
        setTimeout(() => {
          navigate(`/events/${eventId}`);
        }, 2000);
      }
    } catch (error) {
      setResult({
        success: false,
        message: error.message || 'Check-in failed',
      });
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="checkin-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="checkin-container">
        <div className="error-message">Event not found</div>
        <button className="btn btn-primary" onClick={() => navigate('/home')}>
          Go Home
        </button>
      </div>
    );
  }

  return (
    <div className="checkin-container">
      <div className="checkin-card">
        <h1>Check In</h1>
        <h2>{event.title}</h2>

        {!user ? (
          <div className="checkin-content">
            <p>Please sign in to check in to this event.</p>
            <div className="checkin-actions">
              <button
                className="btn btn-primary"
                onClick={() => navigate('/login', { state: { returnTo: window.location.pathname } })}
              >
                Sign In
              </button>
            </div>
          </div>
        ) : (
          <div className="checkin-content">
            {result ? (
              <div className={`checkin-result ${result.success ? 'success' : 'error'}`}>
                <div className="result-icon">
                  {result.success ? '✅' : '❌'}
                </div>
                <p>{result.message}</p>
                {result.success && (
                  <p className="redirect-message">Redirecting to event...</p>
                )}
              </div>
            ) : (
              <>
                <div className="event-info">
                  <p>
                    <strong>Date:</strong>{' '}
                    {new Date(event.startsAt).toLocaleDateString()}
                  </p>
                  <p>
                    <strong>Time:</strong>{' '}
                    {new Date(event.startsAt).toLocaleTimeString()}
                  </p>
                  {event.location?.text && (
                    <p>
                      <strong>Location:</strong> {event.location.text}
                    </p>
                  )}
                </div>

                {event.rules.bringAGuest > 0 && (
                  <div className="plus-ones-input">
                    <label htmlFor="plusOnes">
                      Number of guests (max {event.rules.bringAGuest})
                    </label>
                    <input
                      type="number"
                      id="plusOnes"
                      min="0"
                      max={event.rules.bringAGuest}
                      value={plusOnes}
                      onChange={(e) => setPlusOnes(parseInt(e.target.value) || 0)}
                      className="input"
                    />
                  </div>
                )}

                <button
                  className="btn btn-primary btn-full"
                  onClick={handleCheckIn}
                  disabled={checkingIn || !token}
                >
                  {checkingIn ? 'Checking in...' : 'Check In'}
                </button>

                {!token && (
                  <p className="error-text">
                    Missing check-in token. Please scan the QR code provided at the event.
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckIn;




